import { EventType } from "@/analytics/analytics-events";
import {
  computeToolPropertyForSelectLocationEvent,
  SelectLocationProperties,
  SelectLocationSources,
} from "@/analytics/generic-events-properties";
import { loadArea } from "@/components/common/project-provider/project-loading-context";
import { isIElementWaypoint } from "@/hooks/use-placeholders";
import { selectDefaultModeFor } from "@/modes";
import {
  areAncestorsMissing,
  areChildrenMissing,
} from "@/store/lightweight-elements-selectors";
import { changeMode } from "@/store/mode-slice";
import { setCompatibleSceneFilterForMode } from "@/store/mode-thunks";
import { selectShouldUseIntensityData } from "@/store/modes/walk-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { TreeData } from "@/utils/tree-generation/tree-data";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import {
  IElement,
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
} from "@faro-lotv/ielement-types";
import {
  addToLoadingRoots,
  isSingleScanSection,
  removeFromLoadingRoots,
  selectIElement,
} from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useCallback } from "react";
import { NodeApi } from "react-arborist";
import { useCaptureTreeContext } from "./capture-tree-context";
import { useCaptureTreeSelectionStore } from "./capture-tree-selection-store";

type SelectNodeCallback = (nodes: Array<NodeApi<TreeData>>) => Promise<void>;

/**
 * Returns a callback invoked when the user clicks an element in the capture tree.
 *
 * When the user clicks a node in the capture tree, the corresponding project element
 * is set as the active element. If the element is an area, the area is fetched and loaded
 * if needed. If the element has a preferred mode to be viewed in (e.g. walk mode for panos,
 * overview mode for pointclouds), the viewer is switched to that mode.
 * If no ielement corresponds to the clicked node, it may be the case that there is a
 * lightweight element for it. If this is the case, the parent area is selected as active
 * element, and loaded.
 *
 * @returns A callback invoked when the user clicks an element in the capture tree
 */
export function useSelectNodeCallback(): SelectNodeCallback {
  const { getState } = useAppStore();
  const dispatch = useAppDispatch();
  const { projectApiClient } = useApiClientContext();

  const activeTool = useAppSelector(selectActiveTool);

  return useCallback(
    async (nodes: Array<NodeApi<TreeData>>) => {
      if (nodes.length !== 1) return;

      const state = getState();

      const node = nodes.at(0);
      if (!node) return;

      const id = findAncestorDatasetIfCluster(node);
      let element = selectIElement(id)(state);
      // The element's children should be available as well, because the user may click a
      // Section/Data Session without children
      // If not all children are available, with the exception of the area, we consider
      // the element as not available
      if (element && areChildrenMissing(element, state)) {
        element = undefined;
      }
      if (element && areAncestorsMissing(element, state)) {
        element = undefined;
      }
      if (!element) {
        // If 'element' is undefined, it may be that the user clicked on a node for which we only have the
        // lightweight ielement. In this case, its parent area must already be loaded as ielement in the store.
        // So we check whether the lightweight element exists for the clicked tree node, we find its parent area,
        // and we set the parent area as the active element so it will be loaded.
        const parentArea = findAncestorArea(node, state);
        if (!parentArea) {
          return;
        }
        dispatch(addToLoadingRoots(parentArea.id));
        await loadArea(parentArea.id, projectApiClient, dispatch);
        dispatch(removeFromLoadingRoots(parentArea.id));
        element = selectIElement(id)(getState());
      }

      if (!element) {
        return;
      }

      // Track selection of scans and clusters
      trackEvent(element, activeTool);

      const shouldUseIntensityData = selectShouldUseIntensityData(getState());
      const elementMode = selectDefaultModeFor(
        element,
        shouldUseIntensityData,
      )(getState());

      if (elementMode) {
        dispatch(changeMode(elementMode.targetMode));
        dispatch(setActiveElement(elementMode.element.id));
        dispatch(
          setCompatibleSceneFilterForMode(
            elementMode.targetMode,
            elementMode.sceneFilters,
          ),
        );
      } else {
        dispatch(setActiveElement(element.id));
      }
    },
    [activeTool, dispatch, getState, projectApiClient],
  );
}

function findAncestorDatasetIfCluster(node: NodeApi<TreeData>): GUID {
  let currentNode: NodeApi<TreeData> | null = node;
  while (currentNode && isCluster(currentNode)) {
    currentNode = currentNode.parent;
  }
  return currentNode ? currentNode.id : node.id;
}

function isCluster(node: NodeApi<TreeData>): boolean {
  const { element } = node.data;
  return (
    (element?.type === IElementType.section &&
      element.typeHint === IElementTypeHint.cluster) ||
    element?.type === IElementType.cluster
  );
}

/**
 * Finds the next ancestor area for a given node in the tree, iterating up along the element's ancestors in the tree.
 *
 * @param node The node for which to find the ancestor area
 * @param state The current state of the application
 * @returns The ancestor area element, or undefined if not found
 */
export function findAncestorArea(
  node: NodeApi<TreeData> | null,
  state: RootState,
): IElement | undefined {
  let currentNode: NodeApi<TreeData> | null = node;
  while (
    currentNode &&
    !(
      currentNode.data.element?.type === IElementType.section &&
      currentNode.data.element.typeHint === IElementTypeHint.area
    )
  ) {
    currentNode = currentNode.parent;
  }
  return selectIElement(currentNode?.id)(state);
}

type CheckboxCallback = (node: NodeApi<TreeData>) => void;

/**
 * @returns The callback executed when the checkbox of a node in the capture tree
 * is clicked
 */
export function useNodeCheckbox(): CheckboxCallback {
  const { selectedIds, multiSelectIds } = useCaptureTreeSelectionStore();

  const { isVisible } = useCaptureTreeContext();

  return useCallback(
    (node: NodeApi<TreeData>) => {
      const currentSelection = new Set(selectedIds);
      if (currentSelection.has(node.id)) {
        currentSelection.delete(node.id);

        // Deselect the parents and all the children
        let parentNode: NodeApi<TreeData> | null = node.parent;
        while (parentNode) {
          const hasSelectedChildren =
            !!parentNode.children &&
            parentNode.children.some(
              (child) => isVisible(child.id) && currentSelection.has(child.id),
            );
          if (hasSelectedChildren) {
            break;
          }

          currentSelection.delete(parentNode.id);
          parentNode = parentNode.parent;
        }

        // Deselect all the children
        const stack: Array<NodeApi<TreeData>> = [...(node.children ?? [])];
        while (stack.length > 0) {
          const childNode = stack.pop();
          if (!childNode) break;
          currentSelection.delete(childNode.id);
          stack.push(...(childNode.children ?? []));
        }
      } else {
        currentSelection.add(node.id);

        // Select the parents and all the children
        let parentNode: NodeApi<TreeData> | null = node.parent;
        while (parentNode) {
          currentSelection.add(parentNode.id);
          parentNode = parentNode.parent;
        }

        const stack: Array<NodeApi<TreeData>> = [...(node.children ?? [])];
        while (stack.length > 0) {
          const childNode = stack.pop();
          if (!childNode) break;
          if (!isVisible(childNode.id)) continue;

          currentSelection.add(childNode.id);
          stack.push(...(childNode.children ?? []));
        }
      }
      return multiSelectIds([...currentSelection]);
    },
    [isVisible, multiSelectIds, selectedIds],
  );
}

function trackEvent(element: IElement, tool: ToolName | null): void {
  if (
    (element.type === IElementType.section &&
      element.typeHint === IElementTypeHint.cluster) ||
    element.type === IElementType.cluster
  ) {
    Analytics.track(EventType.selectCluster);
  } else if (isSingleScanSection(element)) {
    Analytics.track(EventType.selectScan);
  } else if (isIElementWaypoint(element)) {
    Analytics.track<SelectLocationProperties>(EventType.selectLocation, {
      via: SelectLocationSources.projectView,
      tool: computeToolPropertyForSelectLocationEvent(tool),
    });
  } else if (isIElementAreaSection(element)) {
    // Selecting an area opens the overview of its sheet scene
    Analytics.track(EventType.openAreaOverview);
  }
}
